import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

const PAGE_SIZE = 12;

/**
 * Fetch a single product by slug — cached, background refetch.
 */
export function useProductBySlug(slug) {
  return useQuery({
    queryKey: ['product', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('*, categories(name, slug)')
        .eq('slug', slug)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!slug,
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Place an order via the place-order Edge Function.
 * Returns { success, order_id, whatsapp_link }.
 */
export function usePlaceOrder() {
  return useMutation({
    mutationFn: async (payload) => {
      const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/place-order`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Order failed');
      return data;
    },
  });
}

/**
 * Fetch featured products ordered by feature_priority.
 */
export function useFeaturedProducts() {
  return useQuery({
    queryKey: ['products', 'featured'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('*, categories(name, slug)')
        .eq('is_featured', true)
        .order('feature_priority', { ascending: true });

      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Fetch paginated & filtered products (numbered pagination).
 * @param {object} filters – { search, categoryId, sortBy, minPrice, maxPrice, page }
 */
export function useProducts(filters = {}) {
  const { search, categoryId, sortBy, minPrice, maxPrice, page = 1 } = filters;

  return useQuery({
    queryKey: ['products', filters],
    queryFn: async () => {
      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      let query = supabase
        .from('products')
        .select('*, categories(name, slug)', { count: 'exact' })
        .range(from, to);

      // Filters
      if (search) {
        query = query.or(`title.ilike.%${search}%,short_description.ilike.%${search}%`);
      }
      if (categoryId) {
        query = query.eq('category_id', categoryId);
      }
      if (minPrice !== undefined && minPrice !== '') {
        query = query.gte('price', Number(minPrice));
      }
      if (maxPrice !== undefined && maxPrice !== '') {
        query = query.lte('price', Number(maxPrice));
      }

      // Sort
      switch (sortBy) {
        case 'price_asc':
          query = query.order('price', { ascending: true });
          break;
        case 'price_desc':
          query = query.order('price', { ascending: false });
          break;
        case 'newest':
          query = query.order('created_at', { ascending: false });
          break;
        default:
          query = query.order('feature_priority', { ascending: false }).order('created_at', { ascending: false });
      }

      const { data, error, count } = await query;
      if (error) throw error;

      return { 
        data, 
        totalCount: count,
        totalPages: Math.ceil(count / PAGE_SIZE),
        currentPage: page
      };
    },
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Fetch all categories.
 */
export function useCategories() {
  return useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('order', { ascending: true });

      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 10,
  });
}

/**
 * Fetch featured testimonials with related product.
 */
export function useFeaturedTestimonials() {
  return useQuery({
    queryKey: ['testimonials', 'featured'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('testimonials')
        .select('*, products(title, slug, image_path)')
        .eq('is_featured', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Fetch similar products by category, excluding the current product.
 */
export function useSimilarProducts(categoryId, excludeId) {
  return useQuery({
    queryKey: ['products', 'similar', categoryId, excludeId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('*, categories(name, slug)')
        .eq('category_id', categoryId)
        .neq('id', excludeId)
        .order('feature_priority', { ascending: false })
        .limit(8);

      if (error) throw error;
      return data;
    },
    enabled: !!categoryId && !!excludeId,
    staleTime: 1000 * 60 * 5,
  });
}
